import React, { useEffect, useRef } from "react";
import { useMap } from "react-leaflet";
import L from "leaflet";
import MarkerLegend from "./markers/MarkerLegend";

/**
 * Map KEY control.
 * Opens on KEY click; closes on KEY again, outside click, map click, or Escape.
 * Uses Leaflet disableClickPropagation so KEY clicks never open "Set home".
 */
export default function LegendControl({ open, onToggle, onClose }) {
  const map = useMap();
  const rootRef = useRef(null);

  useEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    L.DomEvent.disableClickPropagation(el);
    L.DomEvent.disableScrollPropagation(el);
    return undefined;
  }, []);

  useEffect(() => {
    if (!open) return undefined;

    const close = () => {
      if (typeof onClose === "function") onClose();
      else onToggle();
    };

    const onPointerDown = (e) => {
      if (rootRef.current?.contains(e.target)) return;
      // Map clicks: let Leaflet 'click' close the KEY while SetHome
      // still sees disabled=true. Don't close early on pointerdown.
      if (typeof e.target?.closest === "function" && e.target.closest(".leaflet-container")) {
        return;
      }
      close();
    };

    const onMapClick = () => close();

    const onKeyDown = (e) => {
      if (e.key === "Escape") close();
    };

    document.addEventListener("pointerdown", onPointerDown, true);
    map.on("click", onMapClick);
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      map.off("click", onMapClick);
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [open, map, onClose, onToggle]);

  return (
    <div className="v2-legend" ref={rootRef}>
      <button
        type="button"
        className="v2-key-btn"
        aria-expanded={open}
        aria-controls="v2-legend-panel"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          onToggle();
        }}
      >
        KEY
      </button>

      {open ? (
        <div
          id="v2-legend-panel"
          className="v2-legend-panel"
          role="dialog"
          aria-label="Map key"
          onPointerDown={(e) => e.stopPropagation()}
        >
          <MarkerLegend />
          <hr className="v2-legend-divider" />
          <div className="v2-marker-legend-heading">Catchment areas</div>
          <div className="v2-marker-legend-row v2-marker-legend-row--text">
            <b>Merged</b>: Thick outline
          </div>
          <div className="v2-marker-legend-row v2-marker-legend-row--text">
            <b>Individuals</b>: Faint outlines
          </div>
          <div className="v2-marker-legend-row v2-marker-legend-row--text">
            <b>Priority</b>: P1 darkest → P3 lighter
          </div>
          <hr className="v2-legend-divider" />
          <div className="v2-marker-legend-heading">School list</div>
          <div className="v2-marker-legend-row v2-marker-legend-row--text">
            <span className="v2-legend-faith-sample">Italic name</span>
            {" = faith priority (Open To All)"}
          </div>
        </div>
      ) : null}
    </div>
  );
}
